import { createSlice } from '@reduxjs/toolkit';

import preloaded from '~/preloaded';
import { ROLE_NAMES } from '~/roles';

import type { CLAN_CREATION_STATUSES } from '../../api/const';

import type { PayloadAction } from '@reduxjs/toolkit';
import type { ClientSettingsWithPreset } from '@wg/web2clientapi/browser/getClientSettings';
import type { BUILDINGS, IBuildings, IBuildingSupply } from '~/Actions/ActionSupply';
import type { ACCOUNT_BAN_TYPES } from '~/constants';
import type { IApiAccountSyncResponse } from '~/types/api';
import type { IBalance } from '~/types/declaration';
import type { Nullable } from '~/types/generics';

export type DiffObjectType = Partial<
  Pick<
    ICurrentAccountState,
    | 'accumulativeClanResource'
    | 'activeApplicationsCount'
    | 'activeInvitesCount'
    | 'clanId'
    | 'isBonusActivated'
    | 'leveling'
    | 'roleName'
  >
>;

export type ICurrentAccountState = {
  accumulativeClanResource: Nullable<number>;
  activeApplicationsCount: number;
  activeInvitesCount: number;
  bans: ACCOUNT_BAN_TYPES[];
  clanId: Nullable<number>;
  clanStatus: Nullable<CLAN_CREATION_STATUSES>;
  id: Nullable<number>;
  inClanCooldownTill: Nullable<string>;
  isBonusActivated: Nullable<boolean>;
  isFreeRenameAvailable: boolean;
  isHiddenAccount: boolean;
  joinedAtClan: Nullable<string>;
  leveling: Nullable<number>;
  nextRenameAvailableAt: Nullable<string>;
  nickname: Nullable<string>;
  permissions: Nullable<string[]>;
  roleName: Nullable<ROLE_NAMES>;
  supplyBuildings: IBuildingSupply[];
  clientSettings: Nullable<ClientSettingsWithPreset>;
  balance: Nullable<IBalance>;
};

const initialState: ICurrentAccountState = Object.assign(
  {
    accumulativeClanResource: 0,
    activeApplicationsCount: 0,
    activeInvitesCount: 0,
    bans: [],
    clanId: 0,
    clanStatus: '',
    id: 0,
    inClanCooldownTill: null,
    isBonusActivated: false,
    isFreeRenameAvailable: false,
    isHiddenAccount: false,
    joinedAtClan: '',
    leveling: 1,
    nextRenameAvailableAt: null,
    nickname: '',
    permissions: [],
    roleName: ROLE_NAMES.PRIVATE,
    supplyBuildings: [],
    clientSettings: null,
    balance: null,
  },
  preloaded.currentAccount,
);

const currentAccountSlice = createSlice({
  name: 'currentAccount',
  initialState,
  reducers: {
    updateAccountInfo(state: ICurrentAccountState, action: PayloadAction<Partial<ICurrentAccountState>>) {
      Object.entries(action.payload || {}).forEach(([key, value]) => {
        state[key] = value;
      });
    },

    syncCurrentAccount(state: ICurrentAccountState, action: PayloadAction<Partial<IApiAccountSyncResponse>>) {
      const buildings = action.payload.clan?.buildings || ({} as IBuildings);
      const newBuildings: IBuildingSupply[] = [];

      for (const key in buildings) {
        if (Object.prototype.hasOwnProperty.call(buildings, key)) {
          const building = buildings[key as BUILDINGS];
          newBuildings.push({
            id: building.id,
            level: building.level,
            modifiers: building.modifiers,
            type: building.name,
          });
        }
      }

      state.supplyBuildings = newBuildings;
    },

    updateBalance(state: ICurrentAccountState, action: PayloadAction<IBalance>) {
      state.balance = action.payload;
    },

    decreaseActiveEntriesCount(state: ICurrentAccountState) {
      if (state.clanId && state.activeApplicationsCount > 0) {
        state.activeApplicationsCount -= 1;
        return;
      }

      if (state.activeInvitesCount > 0) {
        state.activeInvitesCount -= 1;
      }
    },

    setClientSettings(state: ICurrentAccountState, action: PayloadAction<ClientSettingsWithPreset>) {
      state.clientSettings = action.payload;
    },
  },
});

export const currentAccountActions = currentAccountSlice.actions;

export default currentAccountSlice.reducer;
